import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm, usePrompt } from '@/components/DialogProvider';
import { errorStatus } from '@/components/RouteError';
import { useToast } from '@/components/ToastProvider';
import { apartmentsQuery } from '@/lib/apartments';
import { errorBlockers, errorCode, errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financePeriodPreviewQuery,
  financePeriodQuery,
  newOperationId,
  useClosePeriod,
  useOperationId,
  useReopenPeriod,
} from '@/lib/finance';
import { formatMinor } from '@/lib/money';
import { STORED_FULL_REASON, correctionsOf, groupPnL, isV2 } from '@/lib/periodSummary';
import { FINANCE_PERIOD_STATUS_TONE } from '@/lib/status-tone';
import type { FinanceCategory, FinanceCloseBlocker, FinancePnLLine, FinanceSnapshot } from '@/api/generated/types.gen';

// Which queue/events screen a given close-blocker code points at (spec §9.1).
// Unknown codes (a future blocker this card hasn't been taught yet) render
// with no link rather than guessing a destination.
function blockerLink(b: FinanceCloseBlocker, month: string) {
  if (b.code === 'planned_expected') return { to: '/finance/queue' as const, search: { month } };
  if (b.code === 'expected_schedule') return { to: '/finance/queue' as const, search: { source: 'schedule' as const, month } };
  // OH-183 slice-3 tails, item 12: previous_month_open names a DIFFERENT
  // month than the one being closed — the card's own `month` prop is the
  // wrong value here, unlike every other case above. b.month (OH-183 item
  // 12, wire shape YYYY-MM-01, finance.periods.tsx's own D-C1 note) is the
  // month actually blocking the close; the queue's schedule source is
  // where an operator clears an expected_schedule/refunds_pending row —
  // whatever specifically kept THAT month open, the queue is still the
  // first place to look. No link at all if the server ever sends this code
  // without a month (should not happen — close.go always sets it — but a
  // missing link beats one pointing at the wrong month).
  if (b.code === 'previous_month_open') {
    if (!b.month) return null;
    return { to: '/finance/queue' as const, search: { source: 'schedule' as const, month: b.month.slice(0, 7) } };
  }
  // Ruling FR-18 (browser smoke S2): unprocessed provider money that belongs
  // to a booking is confirmed on that booking's schedule row, never on
  // Events. Any month (§9.1) — no month filter, or a September capture that
  // blocks August would sit outside the filtered list.
  if (b.code === 'provider_facts_on_schedule') return { to: '/finance/queue' as const, search: { source: 'schedule' as const } };
  if (
    // provider_facts_unprocessed counts only facts WITHOUT a booking/schedule
    // row (FR-18) — exactly what Events' «Provider money» lists.
    b.code === 'provider_facts_unprocessed' ||
    b.code === 'refund_attempts_stale_or_contradicted' ||
    // D-M7 (final review): refunds_pending (open refund targets of
    // cancellations in the month) has no per-booking id on the blocker
    // (FinanceCloseBlocker is code/count/detail only), so Events — the
    // refund node's own screen — is the natural destination, same as the
    // other two refund/provider-fact blockers already routed there.
    b.code === 'refunds_pending'
  ) {
    return { to: '/finance/events' as const, search: undefined };
  }
  // D-M7 (final review): the remaining two natural destinations close.go's
  // collectCloseBlockers can produce.
  if (b.code === 'rules_not_materialized') return { to: '/finance/rules' as const, search: undefined };
  if (b.code === 'planned_submissions') return { to: '/finance/submissions' as const, search: undefined };
  // Preview-only codes (spec 2026-10-21 §5.4): month_not_ended is cleared by
  // the calendar; stays_pending / stay_sync_failed have no screen of their
  // own — their operator action is spelled out under the line instead
  // (BLOCKER_ACTION below).
  if (b.code === 'month_not_ended' || b.code === 'stays_pending' || b.code === 'stay_sync_failed') return null;
  // Fix round 1, item 7 (review Minor): a code this function has not been
  // taught yet still renders — just without a "View →" link on that one
  // row, the blocker's own `detail`/`count` text is unaffected. Adding a
  // new server-side close blocker means adding its case here too, or an
  // operator sees a plain, unactionable line for it.
  // (unregistered_attachments: no natural single destination — left as is.
  // previous_month_open is handled above, OH-183 item 12.)
  return null;
}

// Spec 2026-10-21 §5.4, ENV-06: what the operator does about the two
// «data not caught up yet» preview blockers — neither has a screen to link to.
const BLOCKER_ACTION: Record<string, string> = {
  stays_pending: 'Press Close: it recalculates them first; or refresh the preview in a minute — the worker recalculates them.',
  stay_sync_failed: 'Try Close; if it keeps refusing, ask an administrator (logs: finance stay sync).',
};

/**
 * One list for both the preview's advisory blockers and a close refusal's
 * (409 close_blocked) — the same links, a different heading. The server's
 * list is shown as returned: the preview may name a previous_month_open the
 * close itself would clear by auto-closing empty earlier months, and this
 * card does not second-guess it.
 */
function BlockerList({ blockers, month, title }: { blockers: readonly FinanceCloseBlocker[]; month: string; title: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3">
      <p className="text-sm font-semibold text-[var(--text-heading)]">{title}</p>
      <ul className="flex flex-col gap-1 text-sm">
        {blockers.map((b) => {
          const link = blockerLink(b, month);
          const action = BLOCKER_ACTION[b.code];
          return (
            <li key={b.code} className="flex flex-col gap-0.5">
              <div className="flex items-center justify-between gap-2">
                <span>
                  {b.detail} <span className="text-muted">({b.count})</span>
                </span>
                {link && (
                  <Link to={link.to} search={link.search} className="shrink-0 text-primary hover:underline">
                    View →
                  </Link>
                )}
              </div>
              {action && <span className="text-xs text-muted">{action}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// formatMinor throws on an amount that is not a safe integer — exactly what a
// corrupted snapshot (plan 2026-10-21 decision 10) can hold. Show such a
// figure raw rather than crash the card that exists to show it.
function money(minor: number, currency: string): string {
  return Number.isSafeInteger(minor) ? formatMinor(minor, currency) : `${minor} (as stored, minor units)`;
}

const SECTION_TITLE = 'mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted';
const ROW = 'border-b border-[var(--border-hairline)]';

function AmountRow({ label, amount, strong = false }: { label: string; amount: number; strong?: boolean }) {
  return (
    <tr>
      <td className={'pt-2 ' + (strong ? 'font-semibold' : 'text-right text-muted')}>{label}</td>
      <td className={'pt-2 text-right tabular-nums ' + (strong ? 'font-bold' : 'font-semibold')}>{money(amount, 'EUR')}</td>
    </tr>
  );
}

interface SummaryProps {
  snapshot: FinanceSnapshot;
  categoryNames: Record<string, string>;
  apartmentNames: Record<string, string>;
  accountNames: Record<string, string>;
  categoriesById: Record<string, FinanceCategory>;
}

/**
 * Spec 2026-10-21 §5.3: one P&L table in groups with subtotals and a Result
 * (Σ base_pnl), then — only when present — corrections (to Σ full_pnl),
 * envelopes, financing and account balances. A preview and a closed month's
 * snapshot render identically. OH-216 adds the result before capital expenses,
 * right above them, when the month has a Capital expenses group.
 *
 * Ids resolve to names via maps cached by other finance screens; a miss falls
 * back to the raw id rather than showing nothing.
 */
function SnapshotSummary({ snapshot, categoryNames, apartmentNames, accountNames, categoriesById }: SummaryProps) {
  const { groups, result, resultBeforeCapex } = groupPnL(snapshot.base_pnl, categoriesById);
  const corr = correctionsOf(snapshot);
  const v2 = isV2(snapshot);
  // «Category · Apartment» (spec §5.3). In the P&L table a financing line sits
  // under its own group heading; in corrections it has none, so it says so.
  const label = (l: FinancePnLLine, markFinancing = false) => {
    const apt = l.apartment_id ? (apartmentNames[l.apartment_id] ?? l.apartment_id) : null;
    if (l.dimension === 'revenue') return ['Accrued revenue', apt].filter(Boolean).join(' · ');
    if (l.dimension === 'ota_commission') return ['Channel commission', apt].filter(Boolean).join(' · ');
    if (l.dimension === 'fx') return 'FX difference';
    const cat = l.category_id ? (categoryNames[l.category_id] ?? l.category_id) : '—';
    let head = l.dimension === 'withheld' ? `${cat} (withheld)` : cat;
    if (markFinancing && l.dimension === 'category' && l.expense_type === 'financing') head = `${head} (financing)`;
    return [head, apt].filter(Boolean).join(' · ');
  };
  const envelopeReport = snapshot.envelope_report ?? [];
  const financing = snapshot.financing ?? [];
  const legacyEnvelopes = [
    snapshot.envelopes.investments != null ? `Investments ${money(snapshot.envelopes.investments, 'EUR')}` : null,
    snapshot.envelopes.fund != null ? `Fund ${money(snapshot.envelopes.fund, 'EUR')}` : null,
  ].filter((x): x is string => x !== null);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <table className="w-full text-sm">
          {groups.map((g) => (
            <Fragment key={g.key}>
              {g.key === 'capex' && resultBeforeCapex !== null && (
                <tbody>
                  <AmountRow label="Result before capital expenses" amount={resultBeforeCapex} strong />
                </tbody>
              )}
              <tbody>
                <tr>
                  <th colSpan={2} className="pt-3 text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                    {g.title}
                  </th>
                </tr>
                {g.lines.map((l, i) => (
                  <tr key={i} className={ROW}>
                    <td className="py-1 pr-3">{label(l)}</td>
                    <td className="py-1 text-right tabular-nums">{money(l.amount_eur_minor, 'EUR')}</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-1 text-right text-muted">Subtotal</td>
                  <td className="py-1 text-right font-semibold tabular-nums">{money(g.subtotal, 'EUR')}</td>
                </tr>
              </tbody>
            </Fragment>
          ))}
          <tfoot>
            <AmountRow label="Result" amount={result} strong />
          </tfoot>
        </table>
        {!v2 && (
          <p className="mt-2 text-xs text-muted">In snapshots before 2026-10, channel withholdings are inside the category expenses.</p>
        )}
      </div>

      {corr.kind === 'rows' && (
        <section>
          <h3 className={SECTION_TITLE}>Corrections</h3>
          <table className="w-full text-sm">
            <tbody>
              {corr.rows.map((r, i) => (
                <tr key={i} className={ROW}>
                  <td className="py-1 pr-3">
                    <div>{label(r.line, true)}</div>
                    <div className="text-xs text-muted">{r.label}</div>
                  </td>
                  <td className="py-1 text-right tabular-nums">{money(r.line.amount_eur_minor, 'EUR')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <AmountRow label="Total" amount={corr.total} />
              <AmountRow label="Result with corrections" amount={corr.fullResult} strong />
            </tfoot>
          </table>
        </section>
      )}

      {corr.kind === 'stored_full' && (
        <section>
          <p className="mb-2 text-sm text-[var(--ollio-warning)]">{STORED_FULL_REASON[corr.reason]}</p>
          <h3 className={SECTION_TITLE}>Full P&amp;L (as stored)</h3>
          <table className="w-full text-sm">
            <tbody>
              {corr.lines.map((l, i) => (
                <tr key={i} className={ROW}>
                  <td className="py-1 pr-3">{label(l, true)}</td>
                  <td className="py-1 text-right tabular-nums">{money(l.amount_eur_minor, 'EUR')}</td>
                </tr>
              ))}
            </tbody>
            {Number.isFinite(corr.fullResult) && (
              <tfoot>
                <AmountRow label="Result with corrections" amount={corr.fullResult} strong />
              </tfoot>
            )}
          </table>
        </section>
      )}

      {v2 && envelopeReport.length > 0 && (
        <section>
          <h3 className={SECTION_TITLE}>Envelopes</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted">
                  <th className="py-1 pr-3 text-left font-normal">Envelope</th>
                  <th className="py-1 pr-3 text-right font-normal">Opening</th>
                  <th className="py-1 pr-3 text-right font-normal">In</th>
                  <th className="py-1 pr-3 text-right font-normal">Out</th>
                  <th className="py-1 text-right font-normal">Closing</th>
                </tr>
              </thead>
              <tbody>
                {envelopeReport.map((e) => (
                  <tr key={e.envelope_id} className={ROW}>
                    <td className="py-1 pr-3">{e.name}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{money(e.opening_eur_minor, 'EUR')}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{money(e.in_eur_minor, 'EUR')}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{money(e.out_eur_minor, 'EUR')}</td>
                    <td className="py-1 text-right font-semibold tabular-nums">{money(e.closing_eur_minor, 'EUR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!v2 && legacyEnvelopes.length > 0 && (
        <section>
          <h3 className={SECTION_TITLE}>Envelopes</h3>
          <p className="text-sm">
            <span className="text-muted">Envelope balance at month end: </span>
            <span className="tabular-nums">{legacyEnvelopes.join(' · ')}</span>
          </p>
        </section>
      )}

      {v2 && financing.length > 0 && (
        <section>
          <h3 className={SECTION_TITLE}>Financing</h3>
          <table className="w-full text-sm">
            <tbody>
              {financing.map((f, i) => (
                <tr key={f.category_id ?? `none-${i}`} className={ROW}>
                  <td className="py-1 pr-3">{f.category_id ? (categoryNames[f.category_id] ?? f.category_id) : '—'}</td>
                  <td className="py-1 text-right tabular-nums">{money(f.amount_eur_minor, 'EUR')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <AmountRow label="Total" amount={financing.reduce((sum, f) => sum + f.amount_eur_minor, 0)} />
            </tfoot>
          </table>
        </section>
      )}

      {snapshot.balances.length > 0 && (
        <section>
          <h3 className={SECTION_TITLE}>Balances</h3>
          <table className="w-full text-sm">
            <tbody>
              {snapshot.balances.map((b, i) => (
                <tr key={i} className={ROW + ' last:border-0'}>
                  <td className="py-1">{accountNames[b.account_id] ?? b.account_id}</td>
                  <td className="py-1 text-right tabular-nums">{money(b.balance_minor, b.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export interface PeriodCloseCardProps {
  month: string;
  canClose: boolean;
  /** super_admin AND this is the latest closed month (checked by the caller — this card trusts it). */
  canReopen: boolean;
}

/**
 * The selected month's close/reopen affordances and its summary (spec §9.2;
 * OH-195 spec 2026-10-21 §5.3–5.4). A closed month shows its frozen snapshot;
 * an open one — or a month with no period row yet (404) — opens straight on
 * the advisory preview: the would-be snapshot plus what blocks the close, so
 * the owner never closes blind. «Close month» therefore waits until that
 * preview's summary is on screen — the preview AND the categories it is
 * grouped by, loaded or failed (a failed category read groups by sign) — and
 * is disabled while the preview is being fetched again; when the preview
 * fails, only «Close without a preview…» is offered, behind a confirm that
 * names the failure; when the period read itself fails (anything but 404) the
 * month's status is unknown, so neither the status badge, nor the preview,
 * nor any Close is shown. A 409 close_blocked's `blockers` array
 * (Problem's own extension) replaces the preview's list with the same links;
 * a 409 retry_close means a booking changed mid-close and the operator just
 * tries again.
 */
export function PeriodCloseCard({ month, canClose, canReopen }: PeriodCloseCardProps) {
  const period = useQuery(financePeriodQuery(month));
  // Spec §5.4: a month without a period row (404) is previewed as open; any
  // other failure of the period read is an error, not a preview.
  const periodMissing = errorStatus(period.error) === 404;
  const periodFailed = period.isError && !periodMissing;
  const status = period.data?.status ?? 'open';
  const preview = useQuery({
    ...financePeriodPreviewQuery(month),
    enabled: !period.isLoading && !periodFailed && status !== 'closed',
  });
  // A 409 period_closed from the preview is not an error: the month was just
  // closed (the preview refetch beat the period refetch) or this tab is stale.
  // Re-read the period so the card shows the stored snapshot (spec §5.4).
  const previewSaysClosed = errorCode(preview.error) === 'period_closed';
  const previewFailed = preview.isError && !previewSaysClosed;
  const qc = useQueryClient();
  useEffect(() => {
    if (previewSaysClosed) void qc.invalidateQueries({ queryKey: financePeriodQuery(month).queryKey, exact: true });
  }, [previewSaysClosed, qc, month]);
  // D-M8 (final review): id -> name lookups for the snapshot tables below —
  // fetched here regardless of whether a snapshot exists yet (cheap, and
  // usually already cached from other finance screens in the session).
  // Switched-off categories included: an old snapshot may name them.
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  // Grouping needs the category kinds (every kind but `expense` groups as
  // income): the summary waits for them, and says so when it has to fall back
  // to grouping by sign.
  const categoriesQuery = useQuery(financeCategoriesQuery(true));
  const categories = categoriesQuery.data ?? [];
  const { data: apartments = [] } = useQuery(apartmentsQuery);
  const accountNames = Object.fromEntries(accounts.map((a) => [a.id, a.name]));
  const categoriesById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const categoryNames = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const apartmentNames = Object.fromEntries(apartments.map((a) => [a.id, a.title]));
  const close = useClosePeriod();
  const reopen = useReopenPeriod();
  // Fix round 1, item 3 (T23-3) + N6 (final review, controller correction):
  // reopen's reason is retyped by the operator on EVERY attempt (askPrompt
  // has no memory of the previous text) — reusing the same operation_id for
  // a retry whose reason actually changed sends a DIFFERENT request under
  // an already-used key, which the server's idempotency store answers with
  // operation_conflict instead of replaying. useOperationId's renew() only
  // takes effect on the NEXT render (too late to read back within the same
  // onReopenClick call), so the id-to-send is computed synchronously via
  // refs instead: the SAME reason as last time keeps the SAME id (a genuine
  // retry after a lost response still replays); a CHANGED reason mints a
  // fresh one right here, before the request goes out.
  const reopenOperationIdRef = useRef(newOperationId());
  const lastReopenReasonRef = useRef<string | null>(null);
  // C-M6 (final review, SPA half): close now honours operation_id too. Its
  // body carries no operator-editable content (only {operation_id} — the
  // month itself forces a remount via finance.periods.tsx's key={month}),
  // so the per-mount-stable-then-renew-on-success shape (useOperationId,
  // same as reopen used to be) is already correct for N6's "renew when the
  // request changes" — there is nothing here that can change within a mount.
  const [closeOperationId, renewCloseOperationId] = useOperationId();
  const askPrompt = usePrompt();
  const askConfirm = useConfirm();
  const { toast } = useToast();
  const [blockers, setBlockers] = useState<FinanceCloseBlocker[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (period.isLoading) return <p className="text-sm text-muted">Loading…</p>;
  const tone = FINANCE_PERIOD_STATUS_TONE[status];
  // A failed period read hides the preview too: «the month is not closed» is a
  // status claim, and a failed refetch keeps the old preview's data.
  const isPreview = !periodFailed && status !== 'closed';
  const snapshot = isPreview ? preview.data?.snapshot : period.data?.snapshot;
  const previewBlockers = isPreview ? (preview.data?.blockers ?? []) : [];
  // Only a month known to be open can be closed — never while its period read
  // fails. «Close month» needs this month's preview on screen and not one whose
  // latest read failed (a failed refetch keeps the old data), and the
  // categories settled, loaded or failed: until then the summary below is only
  // «Loading categories…», with no result to close on. A failed preview leaves
  // only the confirmed close below, which has no summary to wait for.
  const closable = canClose && !periodFailed && status === 'open';
  const closeOnPreview = closable && preview.data !== undefined && !preview.isError && !categoriesQuery.isPending;
  const closeWithoutPreview = closable && previewFailed;
  // A preview being fetched again (a focus refetch, an invalidation after
  // another month's close) is about to be replaced: neither Close can be
  // pressed until it lands.
  const previewRefreshing = isPreview && preview.isRefetching;

  function onCloseClick() {
    setBlockers(null);
    setError(null);
    close.mutate(
      { month, operationId: closeOperationId },
      {
        onSuccess: () => {
          renewCloseOperationId();
          toast({ kind: 'success', title: 'Month closed' });
        },
        onError: (err) => {
          const bl = errorBlockers(err);
          if (bl && bl.length > 0) {
            setBlockers(bl);
          } else if (errorCode(err) === 'retry_close') {
            toast({ kind: 'error', title: 'Bookings changed under the close — try again' });
          } else {
            // FR-2 (SPA half): a 409 month_not_ended lands here too — its
            // `detail` (PART 1: names the month and the first day it can be
            // closed) is exactly what errorMessage(err) surfaces, same as
            // any other close error this card doesn't special-case.
            setError(errorMessage(err));
          }
        },
      },
    );
  }

  async function onCloseWithoutPreviewClick() {
    const ok = await askConfirm({
      title: 'Close without a preview?',
      body:
        `The preview could not be computed: ${errorMessage(preview.error)}\n` +
        'The close freezes what the server computes now — you will not have seen it first.',
      confirmLabel: 'Close month',
    });
    if (ok) onCloseClick();
  }

  async function onReopenClick() {
    const reason = await askPrompt({ title: 'Reopen this month', label: 'Why?' });
    if (!reason?.trim()) return;
    const trimmed = reason.trim();
    if (lastReopenReasonRef.current !== null && lastReopenReasonRef.current !== trimmed) {
      reopenOperationIdRef.current = newOperationId();
    }
    lastReopenReasonRef.current = trimmed;
    const operationId = reopenOperationIdRef.current;
    reopen.mutate(
      { month, reason: trimmed, operationId },
      {
        onSuccess: () => {
          reopenOperationIdRef.current = newOperationId();
          lastReopenReasonRef.current = null;
        },
        onError: (err) => toast({ kind: 'error', title: 'Could not reopen', message: errorMessage(err) }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg font-bold text-[var(--text-heading)]">{month}</h2>
        {!periodFailed && <Badge variant={tone.variant}>{tone.label}</Badge>}
      </div>

      {closeOnPreview && (
        <div>
          <Button variant="primary" onClick={onCloseClick} loading={close.isPending || previewRefreshing}>
            Close month
          </Button>
        </div>
      )}
      {closeWithoutPreview && (
        <div>
          <Button
            variant="secondary"
            onClick={() => void onCloseWithoutPreviewClick()}
            loading={close.isPending || previewRefreshing}
          >
            Close without a preview…
          </Button>
        </div>
      )}

      {blockers && blockers.length > 0 ? (
        <BlockerList blockers={blockers} month={month} title="Blocked — clear these first" />
      ) : (
        previewBlockers.length > 0 && <BlockerList blockers={previewBlockers} month={month} title="What blocks the close" />
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {periodFailed && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(period.error)}
        </p>
      )}

      {isPreview && preview.isLoading && <p className="text-sm text-muted">Loading the preview…</p>}
      {previewRefreshing && <p className="text-sm text-muted">Refreshing the preview…</p>}
      {isPreview && previewFailed && (
        <p role="alert" className="text-sm text-destructive">
          Could not load the preview: {errorMessage(preview.error)}
        </p>
      )}

      {isPreview && preview.data && (
        <div>
          <Badge variant="warning">Preview — the month is not closed</Badge>
        </div>
      )}

      {snapshot && categoriesQuery.isPending && <p className="text-sm text-muted">Loading categories…</p>}
      {snapshot && categoriesQuery.isError && (
        <p className="text-xs text-muted">Categories couldn&apos;t be loaded — lines grouped by sign</p>
      )}
      {snapshot && !categoriesQuery.isPending && (
        <SnapshotSummary
          snapshot={snapshot}
          categoryNames={categoryNames}
          apartmentNames={apartmentNames}
          accountNames={accountNames}
          categoriesById={categoriesById}
        />
      )}

      {canReopen && status === 'closed' && (
        <div>
          <Button variant="secondary" onClick={() => void onReopenClick()} loading={reopen.isPending}>
            Reopen
          </Button>
        </div>
      )}
    </div>
  );
}
